<script setup lang="ts">
import { provideUseId } from '@headlessui/vue'
import { Analytics } from '@vercel/analytics/nuxt'
import type { Database } from '~/types'

const user = useSupabaseUser()
const supabase = useSupabaseClient<Database>()
provideUseId(() => useId().replace(/[-:]/, '_'))

// Handle OAuth callback - capture provider tokens from session
onMounted(async () => {
  // The OAuth code exchange already completed in the callback route before
  // this mounts, so the session can be read immediately — no artificial wait.
  const { data: { session } } = await supabase.auth.getSession()
  
  if (session?.provider_token && session?.provider_refresh_token && user.value) {
    console.log('[App] OAuth session detected with provider tokens')
    
    // Check if tokens already exist in database
    const { data: existingTokens } = await supabase
      .from('tokens_google')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle()
    
    if (!existingTokens) {
      console.log('[App] Storing OAuth tokens in database...')
      
      // Calculate expiry time (default to 1 hour from now if not provided)
      const expiresAt = session.expires_at 
        ? new Date(session.expires_at * 1000).toISOString()
        : new Date(Date.now() + 3600 * 1000).toISOString()
      
      const { error } = await supabase.from('tokens_google').upsert({
        id: session.user.id,
        refresh_token: session.provider_refresh_token,
        access_token: session.provider_token,
        expires: expiresAt
      })
      
      if (error) {
        console.error('[App] Failed to store tokens:', error)
      } else {
        console.log('[App] Tokens stored successfully!')
      }
    } else {
      console.log('[App] Tokens already exist in database')
    }
  }
})
</script>

<template>
  <Analytics />
  <TProviderDialog />
  <TProviderToast />
  <NuxtLayout :name="user ? 'default' : 'empty'">
    <NuxtPage />
  </NuxtLayout>
</template>


<style lang="sass">
body, .scrollbar, *
  --sb-track-color: #373d40
  --sb-thumb-color: #ffffff
  --sb-size: 6px
  &::-webkit-scrollbar
    width: var(--sb-size)
    height: var(--sb-size)
  &::-webkit-scrollbar-track
    background: var(--sb-track-color)
    border-radius: 16px
  &::-webkit-scrollbar-thumb
    background: var(--sb-thumb-color)
    border-radius: 16px
  @supports not selector(::-webkit-scrollbar)
    scrollbar-color: var(--sb-thumb-color) var(--sb-track-color)

</style>